import { useState } from 'react';
import { Share2 } from 'lucide-react';
import { createTrainingShareLinkApi } from '../../lib/shareLinksApi';

type Props = {
  resourceType: 'exercise' | 'plan' | 'session';
  resourceId: string;
  title: string;
  accessCode?: string;
  shareKey?: string;
  compact?: boolean;
  iconOnly?: boolean;
};

const fallbackCopy = (value: string): boolean => {
  const element = document.createElement('textarea');
  element.value = value;
  element.style.position = 'fixed';
  element.style.opacity = '0';
  document.body.appendChild(element);
  element.select();
  try { return document.execCommand('copy'); } catch { return false; } finally { element.remove(); }
};

export default function TrainingShareButton({ resourceType, resourceId, title, accessCode, shareKey, compact = false, iconOnly = false }: Props) {
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true); setMessage('Deellink maken…');
    try {
      const url = await createTrainingShareLinkApi({ resourceType, resourceId, accessCode, shareKey });
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          setMessage('Deellink is klaar om te delen.');
          return;
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') { setMessage(null); return; }
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        setMessage('Deellink gekopieerd.');
      } catch {
        setMessage(fallbackCopy(url) ? 'Deellink gekopieerd.' : `Deellink gemaakt: ${url}`);
      }
    } catch {
      setMessage('Deellink kon niet worden gemaakt. Probeer het opnieuw.');
    } finally { setBusy(false); }
  };

  return (
    <span className="relative inline-flex flex-col items-end gap-1">
      <button type="button" disabled={busy} onClick={() => void share()} aria-label={iconOnly ? 'Delen' : undefined} title={iconOnly ? 'Delen' : undefined} className={`inline-flex min-h-11 items-center justify-center gap-2 border border-gray-300 text-sm font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-700 disabled:opacity-60 ${iconOnly ? 'min-w-11 rounded-full px-0' : compact ? 'px-3' : 'px-4'}`}><Share2 size={17} />{!iconOnly && (compact ? 'Delen' : 'Deellink maken')}</button>
      {message && <span className={iconOnly ? 'absolute right-0 top-full z-10 mt-1 w-max max-w-[min(18rem,calc(100vw-2rem))] border border-gray-200 bg-white px-2 py-1.5 text-right text-xs font-medium text-gray-600 shadow-md' : 'max-w-xs text-right text-xs font-medium text-gray-600'} role="status">{message}</span>}
    </span>
  );
}
